import React from 'react';

import PropTypes from 'prop-types';

import A from 'wanderu-component-react-router-link';
import {
    Image,
    Section,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import cgConfig from '../../../../config/carrierguide.locale';

import styles from './RouteMap.scss';

function formatDate(i18nMoment, date = '') {
    const dateIsUTC = date.includes('Z');
    const format = dateIsUTC
        ? ''
        : 'YYYY-MMM-DD HH:mm Z';
    const formattedDate = i18nMoment(date, format);

    if (!dateIsUTC && i18nMoment().isDST()) {
        formattedDate.add(1, 'hour');
    }

    return formattedDate;
}

function RouteRow(props) {
    const {
        i18nMoment,
        route = {},
        cityTable,
    } = props;
    const {
        city_name: cityName,
        state_name: stateName,
        city_guide_url_slug: cityGuideUrl,
        state_guide_url_slug: stateGuideUrl,
        top_city_guide_url_slug: topCityUrl,
        daily_trips: dailyTrips,
        earliest_arrival: earliestArrival,
        latest_arrival: latestArrival,
        city_count: citiesServed,
        trips_per_day: tripsPerDay,
        top_city_name: topCityName,
    } = route;

    const tableCellProps = {
        size: 'small',
    };
    const earliestArrivalDate = formatDate(i18nMoment, earliestArrival);
    const latestArrivalDate = formatDate(i18nMoment, latestArrival);

    return (
        <TableRow>
            <TableCell size="small">
                <A href={cityTable
                    ? cityGuideUrl
                    : stateGuideUrl}>
                    {cityTable
                        ? cityName
                        : stateName}
                </A>
            </TableCell>
            <Typography
                component={TableCell}
                componentProps={tableCellProps}
            >
                {cityTable
                    ? dailyTrips
                    : citiesServed}
            </Typography>
            <Typography
                component={TableCell}
                componentProps={tableCellProps}
            >
                {cityTable
                    ? earliestArrivalDate.format('h:mm a')
                    : Math.round(tripsPerDay)}
            </Typography>
            {cityTable
                ? (
                    <Typography
                        component={TableCell}
                        componentProps={tableCellProps}
                    >
                        {latestArrivalDate.format('h:mm a')}
                    </Typography>
                )
                : (
                    <TableCell size="small">
                        <A href={topCityUrl}>
                            {topCityName}
                        </A>
                    </TableCell>
                )}
        </TableRow>
    );
}
RouteRow.propTypes = {
    cityTable: PropTypes.bool,
    i18nMoment: PropTypes.func.isRequired,
    route: PropTypes.object.isRequired,
};

const LocalizedRouteRow = localize(RouteRow);

function RouteTable(props) {
    const {
        cityTable = false,
        headerTitles,
        topStats,
    } = props;

    const headerProps = {
        size: 'small',
        variant: 'head',
    };

    return (
        <Table variant="blocks">
            <TableHead>
                <TableRow variant="head">
                    {headerTitles.map((title) => (
                        <Typography
                            component={TableCell}
                            componentProps={headerProps}
                            key={title}
                        >
                            {title}
                        </Typography>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {topStats.map((route) => (
                    <LocalizedRouteRow
                        cityTable={cityTable}
                        key={cityTable
                            ? route?.wcityid
                            : route?.state_name}
                        route={route}
                    />
                ))}
            </TableBody>
        </Table>
    );
}

RouteTable.propTypes = {
    cityTable: PropTypes.bool,
    headerTitles: PropTypes.arrayOf(PropTypes.string).isRequired,
    topStats: PropTypes.arrayOf(PropTypes.object).isRequired,
};

function RouteMap(props) {
    const {
        alt = '',
        metrics = {},
        showCities = false,
        showStates = false,
        src = '',
        t,
        topRoutes = {},
    } = props;
    const {
        cities,
        states,
    } = topRoutes;
    const {
        stations,
    } = metrics;

    if (!cities && !states) {
        return null;
    }

    const cityTableHeaders = [
        t('cityTableHeader'),
        t('routeTableHeader'),
        t('earliestTableHeader'),
        t('latestTableHeader'),
    ];

    const stateTableHeaders = [
        t('stateTableHeader'),
        t('citiesServedTableHeader'),
        t('tripsTableHeader'),
        t('topCityTableHeader'),
    ];

    return (
        <Section aria-labelledby="route-map-header">
            <Typography
                className={styles.title}
                component="h2"
                id="route-map-header"
                variant="h3"
            >
                {t('mapTitle')}
            </Typography>
            <div className={styles.infoSection}>
                {!!src && (
                    <Image
                        alt={alt}
                        className={styles.routeMapImage}
                        height={200}
                        loading="lazy"
                        src={src}
                        width={310}
                    />
                )}
                <Typography>
                    {t('routeMapDescription', { stationCount: stations })}
                </Typography>
            </div>
            <div className={styles.tableContainer}>
                {!!cities && showCities && (
                    <>
                        <Typography
                            className={styles.tableTitle}
                            component="h3"
                            variant="subtitle"
                        >
                            {t('topCitiesTableTitle')}
                        </Typography>
                        <RouteTable
                            headerTitles={cityTableHeaders}
                            topStats={cities}
                            cityTable
                        />
                    </>
                )}
                {!!states && showStates && (
                    <>
                        <Typography
                            className={`${styles.tableTitle} ${styles.statesTable}`}
                            component="h3"
                            variant="subtitle"
                        >
                            {t('topStatesTableTitle')}
                        </Typography>
                        <RouteTable
                            headerTitles={stateTableHeaders}
                            topStats={states}
                        />
                    </>
                )}
            </div>
        </Section>
    );
}

RouteMap.propTypes = {
    alt: PropTypes.string,
    metrics: PropTypes.shape({
        stations: PropTypes.number,
    }),
    showCities: PropTypes.bool,
    showStates: PropTypes.bool,
    src: PropTypes.string,
    t: PropTypes.func.isRequired,
    topRoutes: PropTypes.shape({
        cities: PropTypes.arrayOf(PropTypes.object),
        states: PropTypes.arrayOf(PropTypes.object),
    }),
};

export {
    RouteMap,
    RouteRow,
    RouteTable,
};
export default localize(RouteMap, cgConfig.namespace);
